import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Tag, X, Plus, Search } from 'lucide-react';
import { AVAILABLE_INTERESTS } from '../constants/interests';

interface InterestSelectorProps {
  /** Preferred prop */
  selectedInterests?: string[];
  /** Back-compat alias (if parent uses `selected`) */
  selected?: string[];
  onChange: (interests: string[]) => void;
  availableInterests?: string[];
  allowCustom?: boolean;
}

const InterestSelector: React.FC<InterestSelectorProps> = ({
  selectedInterests,
  selected, // alias
  onChange,
  availableInterests = [...AVAILABLE_INTERESTS],
  allowCustom = false
}) => {
  // Always work with a safe array
  const picked: string[] = Array.isArray(selectedInterests)
    ? selectedInterests
    : Array.isArray(selected)
    ? selected
    : [];

  const [customInterest, setCustomInterest] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filteredSuggestions = availableInterests.filter((interest) => {
    return !picked.includes(interest);
  });

  // Check scroll position to show/hide fade indicators
  const checkScroll = useCallback(() => {
    const list = listRef.current;
    if (!list) return;

    const hasScroll = list.scrollHeight > list.clientHeight;
    if (!hasScroll) {
      setCanScrollUp(false);
      setCanScrollDown(false);
      return;
    }

    const scrollTop = list.scrollTop;
    const scrollBottom = list.scrollHeight - list.clientHeight - scrollTop;

    setCanScrollUp(scrollTop > 10);
    setCanScrollDown(scrollBottom > 10);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSuggestions]);

  // Check scroll on mount and when suggestions change
  useEffect(() => {
    if (showSuggestions) {
      setTimeout(checkScroll, 0);
    }
  }, [showSuggestions, filteredSuggestions, checkScroll]);

  const handleRemoveInterest = (interest: string) => {
    onChange(picked.filter(i => i !== interest));
  };

  const handleAddInterest = (interest: string) => {
    if (!picked.includes(interest)) {
      onChange([...picked, interest]);
    }
    setCustomInterest('');
    setShowSuggestions(false);
  };

  const handleAddCustomInterest = () => {
    const trimmed = customInterest.trim();
    if (trimmed && !picked.includes(trimmed)) {
      onChange([...picked, trimmed]);
      setCustomInterest('');
    }
  };

  return (
    <div ref={dropdownRef}>
      <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-3">
        {picked.map((interest) => (
          <div
            key={interest}
            className="bg-primary-100 text-contrast px-2 py-1 rounded-full text-[10px] sm:text-xs flex items-center"
          >
            <Tag size={12} className="mr-1 text-secondary" />
            <span className="truncate max-w-[100px] sm:max-w-[150px]">{interest}</span>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRemoveInterest(interest);
              }}
              className="ml-1 p-1 rounded-full hover:bg-primary-200 text-contrast min-h-[24px] min-w-[24px] flex items-center justify-center"
              aria-label={`Remove ${interest}`}
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>

      <div className="relative">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowSuggestions(!showSuggestions);
          }}
          className="w-full p-3 bg-primary-100 border border-blue-400/30 hover:border-blue-400/50 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-sm text-contrast min-h-[44px] transition-all flex items-center justify-between"
        >
          <div className="flex items-center">
            <Tag size={16} className="text-contrast-500 mr-2" />
            <span className="text-contrast-500">Select interests...</span>
          </div>
          <Search size={16} className="text-contrast-500" />
        </button>

        {allowCustom && (
          <div className="flex mt-2 gap-0">
            <input
              type="text"
              value={customInterest}
              onChange={(e) => setCustomInterest(e.target.value)}
              placeholder="Add a custom interest..."
              className="flex-1 p-2 bg-primary-100 border border-primary-200 rounded-l-md focus:ring-2 focus:ring-secondary focus:border-secondary text-sm text-contrast placeholder-contrast-500 min-h-[44px]"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustomInterest();
                }
              }}
            />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAddCustomInterest();
              }}
              disabled={!customInterest.trim() || picked.includes(customInterest.trim())}
              className="bg-secondary text-white p-2 rounded-r-md hover:bg-secondary-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Add interest"
            >
              <Plus size={18} />
            </button>
          </div>
        )}

        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute z-50 mt-1 w-full bg-primary-50 rounded-md shadow-xl border border-primary-200 bottom-auto top-full">
            {/* Subtle top fade indicator */}
            {canScrollUp && (
              <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-primary-50 to-transparent pointer-events-none z-10 rounded-t-md" />
            )}

            <div
              ref={listRef}
              className="max-h-[40vh] overflow-y-auto"
              onScroll={checkScroll}
            >
              <ul className="py-1">
                {filteredSuggestions.map((interest) => (
                  <li
                    key={interest}
                    className="px-3 py-2 hover:bg-primary-100 cursor-pointer flex items-center text-xs sm:text-sm text-contrast min-h-[44px]"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAddInterest(interest);
                    }}
                  >
                    <Tag size={14} className="text-secondary mr-2" />
                    <span>{interest}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Subtle bottom fade indicator */}
            {canScrollDown && (
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-primary-50 to-transparent pointer-events-none z-10 rounded-b-md" />
            )}
          </div>
        )}
      </div>

      <p className="text-[10px] sm:text-xs text-contrast-500 mt-2">
        {allowCustom
          ? 'Select from suggestions or add your own interests'
          : 'Select from the available interests'}
      </p>
    </div>
  );
};

export default InterestSelector;
